import { CookieOptions, CookieService } from 'ngx-cookie';
import { DEFAULT_LANG } from '@config/app-config';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ILanguage } from '@globalization/interfaces/language.interface';
import { inject, Injectable } from '@angular/core';
import { LogService } from '@logs/services/log.service';
import { Observable, of } from 'rxjs';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import { URLS } from '@globalization/bavel-globalization.config';
import { Injector } from '@angular/core';
const SERVICE_NAME = 'TranslationService';

@Injectable()
export class TranslationService implements TranslateLoader {
  private noExpirationCookieDate: Date = new Date('9999-12-30T23:00:00.000Z');

  private cookieService: CookieService = inject(CookieService);
  private http: HttpClient = inject(HttpClient);
  private logService: LogService = inject(LogService);
  private injector: Injector = inject(Injector);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  public getTranslation(lang: string): Observable<any> {
    this.logService.logInfo(`[${SERVICE_NAME}] Retrieving translation tokens for lang: ${lang}`);
    const params = new HttpParams().set('culture', lang);
    return this.http.get(URLS.TRANSLATIONS, { params });
  }

  public getAvailableLangs(): Observable<ILanguage[]> {
    return this.http.get<ILanguage[]>(URLS.AVAILABLE_LANGS)
      .pipe(
        distinctUntilChanged()
      );
  }

  public getDefaultLang(): Observable<string> {
    return of<string>(DEFAULT_LANG);
  }

  public getCurrentLang(): Observable<string> {
    const lang = this.cookieService.get(environment.languageService.LanguageCookieName) || DEFAULT_LANG;
    return of<string>(lang);
  }

  public setCurrentLang(lang: string): void {
    const preferredLanguage = this.getPreferredLanguage(lang);
    const cookieOptions: CookieOptions = {
      domain: environment.languageService.LanguageCookieDomain,
      httpOnly: false,
      expires: this.noExpirationCookieDate
    };
    this.logService.logInfo(`[${SERVICE_NAME}] Stored lang ${preferredLanguage} in cookie`);
    this.cookieService.put(environment.languageService.LanguageCookieName, preferredLanguage, cookieOptions);
    this.getTranslateService().use(preferredLanguage);
  }

  public getPreferredLanguage(header: string): string {
    return header.split(',')
      .map(item => {
        const [lang, quality] = item.trim().split(';');
        const qualityValue = quality?.split('=')[1];
        const q = qualityValue ? Number(qualityValue): 1;
        return { lang, q }; })
      .sort((a, b) => b.q - a.q)
      .map(item => item.lang)[0];
  }

  private getTranslateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

}
